import { Group, Progress, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useDistributedShares } from 'src/Hooks/Shared/useDistributedShares';
import { AssetGovernanceFields, ShareType } from 'src/types/governance.types';
import { getSharesDistributedLabel } from 'src/Utils/legalStructureVocabulary';

import styles from './SharesDistributedBar.module.scss';

const formatShares = (value: number): string => Number(value.toFixed(2)).toString();

const SharesDistributedBar = () => {
  const { t } = useTranslation();
  const { data: asset } = useFetchAssetDetails();
  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const { distributedShares, totalShares, sharesPercentage, shareType } = useDistributedShares();

  if (!shareType) return null;

  const isPercentageMode = shareType === ShareType.PERCENTAGE;
  const hasDismemberment = governance?.hasDismemberment ?? false;
  const label = getSharesDistributedLabel(t, shareType);
  const valueText = `${formatShares(distributedShares)}${isPercentageMode ? '%' : ''} / ${formatShares(totalShares)}${isPercentageMode ? '%' : ''}`;

  const labelNode = hasDismemberment ? (
    <Tooltip
      label={t(
        'admin.governance.sharesDistributed.dismembermentTooltip',
        "L'usufruit n'entre pas dans le capital : il se superpose à la nue-propriété déjà comptée."
      )}
      withArrow
      multiline
      w={280}
    >
      <Text size="sm" c="dimmed" className={styles.helpCursor}>
        {label}
      </Text>
    </Tooltip>
  ) : (
    <Text size="sm" c="dimmed">
      {label}
    </Text>
  );

  return (
    <div className={styles.container}>
      <Group justify="space-between" mb={4}>
        {labelNode}
        <Text size="sm" fw={500}>
          {valueText}
        </Text>
      </Group>
      <Progress
        value={sharesPercentage}
        color={sharesPercentage >= 100 ? 'green' : 'blue'}
        size="md"
        radius="xl"
      />
    </div>
  );
};

export default SharesDistributedBar;
